import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Chip } from '@mui/material';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import { startCase } from 'lodash';
import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import KeyValueResponsive from '@/components/KeyValueResponsive';
import type { KeyValueResponsiveModel } from '@/components/KeyValueResponsive/KeyValueResponsive.types';
import PriorityChip from '@/components/PriorityChip';
import type { PriorityTypes } from '@/components/PriorityChip/PriorityChip.types';
import { caseDetailsAtom } from '@/pages/Case/Case.store';
import {
  caseAIStatusFormatter,
  getColorByVerdict
} from '@/pages/Case/Case.utils';
import { CASE_AI_STATUS_COLORS, CASE_STATUS_COLORS } from '@/pages/Cases';
import isLayoutMobileAtom from '@/store/layout';
import getDateTime from '@/utils/getDateTime.utils';

import useStyles from './CaseSubTopbarBottom.styles';

const CaseSubTopbarBottom = () => {
  const isLayoutMobile = useAtomValue(isLayoutMobileAtom);
  const { classes } = useStyles({ isLayoutMobile });
  const caseDetails = useAtomValue(caseDetailsAtom);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isInternalCasePage = pathname.includes('/case-internal');

  const handleBack = () => {
    navigate({
      pathname: isInternalCasePage ? '/cases-internal' : '/cases'
    });
  };

  const socPriority = useMemo(
    () => caseDetails?.soc_priority?.split('-')[0].trim(),
    [caseDetails]
  );

  const aiPriority = useMemo(
    () => caseDetails?.ai_priority?.split('-')[0].trim(),

    [caseDetails]
  );

  const kvItems = useMemo(() => {
    const kv: KeyValueResponsiveModel[] = [
      {
        label: 'Priority',
        value: (
          <PriorityChip priority={caseDetails?.priority as PriorityTypes} />
        )
      },
      {
        label: 'Status',
        value: caseDetails?.ai_status ? (
          <Chip
            label={
              caseDetails?.ai_status !== 'n/a'
                ? caseAIStatusFormatter(caseDetails?.ai_status as string)
                : 'N/A'
            }
            variant="outlined"
            sx={{ fontWeight: 'normal' }}
            color={
              CASE_AI_STATUS_COLORS[
                caseAIStatusFormatter(
                  caseDetails?.ai_status as string
                ) as keyof typeof CASE_AI_STATUS_COLORS
              ] || 'default'
            }
          />
        ) : (
          '--'
        )
      },
      {
        label: 'AI Verdict',
        value: caseDetails?.ai_verdict ? (
          <Chip
            label={startCase(caseDetails?.ai_verdict as string)}
            variant="twoTone"
            sx={{ fontWeight: 'normal' }}
            color={getColorByVerdict(caseDetails?.ai_verdict || '')}
          />
        ) : (
          '--'
        )
      },
      {
        label: 'Category',
        value: caseDetails?.customer_category ? (
          <Chip label={caseDetails?.customer_category as string} />
        ) : (
          '--'
        )
      },
      {
        label: 'Scope Ticket',
        value: caseDetails?.scope_ticket_id ? (
          <Link to={`/ticket/${caseDetails?.scope_ticket_id}`}>
            {caseDetails?.scope_ticket_id}
          </Link>
        ) : (
          '--'
        )
      },
      {
        label: 'Last Updated',
        value: caseDetails?.soc_last_updated_at
          ? getDateTime(caseDetails?.soc_last_updated_at)
          : '--'
      },
      {
        label: 'Created At',
        value: caseDetails?.created_at
          ? getDateTime(caseDetails?.created_at)
          : '--'
      }
    ];

    const kvInternal: KeyValueResponsiveModel[] = [
      {
        label: 'Queue Priority',
        value: <PriorityChip priority={socPriority as PriorityTypes} />
      },
      {
        label: 'Ticket Priority',
        value: (
          <PriorityChip
            priority={
              (caseDetails?.ticket?.ticket_priority?.name as PriorityTypes) ||
              '--'
            }
          />
        )
      },
      {
        label: 'AI Priority',
        value: <PriorityChip priority={aiPriority as PriorityTypes} />
      },
      {
        label: 'SOC Status',
        value: (
          <Chip
            label={caseDetails?.soc_status as string}
            variant="outlined"
            sx={{ fontWeight: 'normal' }}
            color={
              CASE_STATUS_COLORS[
                caseDetails?.soc_status as keyof typeof CASE_STATUS_COLORS
              ] || 'default'
            }
          />
        )
      },
      {
        label: 'AI Status',
        value: caseDetails?.ai_status ? (
          <Chip
            label={
              caseDetails?.ai_status !== 'n/a'
                ? caseAIStatusFormatter(caseDetails?.ai_status as string)
                : 'N/A'
            }
            variant="outlined"
            sx={{ fontWeight: 'normal' }}
            color={
              CASE_AI_STATUS_COLORS[
                caseAIStatusFormatter(
                  caseDetails?.ai_status as string
                ) as keyof typeof CASE_AI_STATUS_COLORS
              ] || 'default'
            }
          />
        ) : (
          '--'
        )
      },
      {
        label: 'SOC Verdict',
        value: (
          <Chip
            label={caseDetails?.soc_verdict as string}
            variant="twoTone"
            sx={{ fontWeight: 'normal' }}
            color={getColorByVerdict(caseDetails?.soc_verdict || '')}
          />
        )
      },
      {
        label: 'AI Verdict',
        value: caseDetails?.ai_verdict ? (
          <Chip
            label={startCase(caseDetails?.ai_verdict as string)}
            variant="twoTone"
            sx={{ fontWeight: 'normal' }}
            color={getColorByVerdict(caseDetails?.ai_verdict || '')}
          />
        ) : (
          '--'
        )
      },
      {
        label: 'Ticket Verdict',
        value: caseDetails?.scope_verdict ? (
          <Chip
            label={caseDetails?.scope_verdict as string}
            variant="outlined"
            color={getColorByVerdict(caseDetails?.scope_verdict || '')}
          />
        ) : (
          '--'
        )
      },
      {
        label: 'SOC Category',
        value: caseDetails?.soc_category ? (
          <Chip label={caseDetails?.soc_category as string} />
        ) : (
          '--'
        )
      },
      {
        label: 'AI Category',
        value: caseDetails?.ai_category ? (
          <Chip label={caseDetails?.ai_category as string} />
        ) : (
          '--'
        )
      },
      {
        label: 'Scope Ticket',
        value: caseDetails?.scope_ticket_id ? (
          <Link to={`/ticket/${caseDetails?.scope_ticket_id}`}>
            {caseDetails?.scope_ticket_id}
          </Link>
        ) : (
          '--'
        )
      },
      {
        label: 'Assignee',
        value: caseDetails?.soc_assignee || '--'
      },
      {
        label: 'Last Updated',
        value: caseDetails?.soc_last_updated_at
          ? getDateTime(caseDetails?.soc_last_updated_at)
          : '--'
      },
      {
        label: 'Created At',
        value: caseDetails?.created_at
          ? getDateTime(caseDetails?.created_at)
          : '--'
      }
    ];

    return isInternalCasePage ? kvInternal : kv;
  }, [caseDetails]);

  return (
    <Box
      className={classNames(classes.caseSubTopbar, classes.caseSubTopbarBottom)}
    >
      <Box className={classes.bottomContent}>
        {!isLayoutMobile && (
          <Button
            onClick={handleBack}
            className={classes.bottomContentBackButton}
            color="secondary"
          >
            <FontAwesomeIcon icon={faArrowLeft} size="lg" />
          </Button>
        )}
        <Box className={classes.bottomContentSection}>
          <KeyValueResponsive items={kvItems} />
        </Box>
      </Box>
    </Box>
  );
};

export default CaseSubTopbarBottom;
